import { useEffect, useState } from 'react'
import posthog from 'posthog-js'
import { faPlain, longJalali } from 'utilities/helper/format'
import { Card } from './Blocks'

/**
 * تاریخچهٔ تعداد سهم (pwa#135) — «از ۱ سهم به ۴ سهم — توسط …», newest first.
 *
 * Manager-only. The server refuses it to anybody else, and the page does not
 * even ask unless the reader is a manager: the rows name the manager who
 * made each change, which is the fund's internal bookkeeping, not something
 * a member reads about themselves.
 *
 * What a row can and cannot say:
 *   * Changes made before this history existed were rebuilt from the Share
 *     rows and حق عضویت snapshots. Those know the counts and roughly when,
 *     never who — so they say «نامشخص» rather than guessing.
 *   * Hamyan support (the Django admin) is named as «پشتیبانی همیان», not as
 *     the agent: a manager has no business learning a support agent's name.
 *   * Share changes are NOT retroactive — a new count counts from the period
 *     it was set in. When that differs from the day the change was made, the
 *     row says from when, because that is the question a manager asks the
 *     next time a حق عضویت looks wrong.
 */

/** How many rows show before «همه». Most members have zero or one change. */
export const SHARE_HISTORY_PREVIEW = 3

const SOURCE_LABEL: { [source: string]: string } = {
    android: 'اپ اندروید',
    ios: 'اپ iOS',
    pwa: 'وب‌اپ',
    web: 'پنل وب',
    excel: 'فایل اکسل',
}

export function changerLabel(
    entry: IShareChangeEntry,
    selfView: boolean
): string {
    if (entry.is_reconstructed) return 'نامشخص (بازسازی‌شده از سابقه)'
    if (entry.source === 'admin') return 'پشتیبانی همیان'
    if (entry.changed_by?.is_self) return selfView ? 'خودتان' : 'خود عضو'
    if (entry.changed_by?.name) return entry.changed_by.name
    if (entry.source === 'excel') return 'ورود از فایل اکسل'
    return 'نامشخص'
}

export function changeLine(entry: IShareChangeEntry): string {
    return `از ${faPlain.format(entry.old_number)} سهم به ${faPlain.format(
        entry.new_number
    )} سهم`
}

export default function ShareHistoryCard({
    history,
    moneypoolId,
    poolshipId,
    selfView,
}: {
    history: IMemberShareHistory
    moneypoolId?: string
    poolshipId?: string
    selfView: boolean
}) {
    const [expanded, setExpanded] = useState(false)
    const entries = history.results ?? []

    useEffect(() => {
        posthog.capture('member_record_share_history_view', {
            fund_id: moneypoolId,
            poolship_id: poolshipId,
            viewer: selfView ? 'self' : 'manager',
            entries_count: entries.length,
            reconstructed_count: entries.filter((e) => e.is_reconstructed)
                .length,
        })
        // once per member opened, not per re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moneypoolId, poolshipId])

    const shown = expanded ? entries : entries.slice(0, SHARE_HISTORY_PREVIEW)
    const hidden = entries.length - shown.length

    return (
        <Card
            title="تاریخچهٔ تعداد سهم"
            aside={
                <span className=" text-[11px] text-text-secondary">
                    الان {faPlain.format(history.number_of_shares ?? 0)} سهم
                </span>
            }
        >
            {entries.length === 0 ? (
                <p className=" mt-2 text-xs leading-6 text-text-secondary">
                    تغییری در تعداد سهم ثبت نشده است.
                </p>
            ) : (
                <ul className=" mt-1 flex flex-col">
                    {shown.map((entry) => {
                        const source = !entry.is_reconstructed
                            ? SOURCE_LABEL[entry.source]
                            : undefined
                        const effectiveDiffers =
                            !!entry.effective_date &&
                            entry.effective_date !== entry.changed_at
                        return (
                            <li
                                key={entry.id}
                                className=" border-b border-divider py-2.5 last:border-b-0"
                            >
                                <p className=" text-xs font-bold text-text-body">
                                    {changeLine(entry)}
                                    <span className=" font-normal text-text-secondary">
                                        {' — توسط '}
                                        {changerLabel(entry, selfView)}
                                    </span>
                                </p>
                                <p className=" mt-0.5 text-[10px] leading-5 text-text-secondary">
                                    {entry.is_reconstructed ? 'حدود ' : ''}
                                    {longJalali(entry.changed_at)}
                                    {source ? ` · از ${source}` : ''}
                                    {effectiveDiffers
                                        ? ` · حساب از ${longJalali(
                                              entry.effective_date
                                          )}`
                                        : ''}
                                </p>
                            </li>
                        )
                    })}
                </ul>
            )}
            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => {
                        posthog.capture('member_record_share_history_expanded', {
                            fund_id: moneypoolId,
                            poolship_id: poolshipId,
                            viewer: selfView ? 'self' : 'manager',
                            entries_count: entries.length,
                        })
                        setExpanded(true)
                    }}
                    className=" mt-2 flex min-h-[44px] w-full items-center justify-center rounded-[10px] bg-canvas px-3 text-xs font-bold text-brand-deep"
                >
                    نمایش همهٔ تغییرها ({faPlain.format(entries.length)})
                </button>
            )}
        </Card>
    )
}
